import {
    Alert,
    Avatar,
    Button,
    DatePicker,
    Drawer,
    Input,
    Modal,
    Radio,
    Select,
    Tabs,
    Tag,
    Tooltip,
    Typography,
    message,
} from "antd";

import {
    BellRing,
    Calendar,
    CalendarClock,
    CheckCircle2,
    FileText,
    MessageCircle,
    Phone,
    PhoneOff,
    Send,
    Stethoscope,
    TriangleAlert,
    X,
} from "lucide-react";
import dayjs, { type Dayjs } from "dayjs";
import TimelineTab from "./TimelineTab";
import CsatTab from "./CsatTab";
import GalleryTab from "./GalleryTab";
import { useEffect, useState } from "react";
import { createAppointmentCareLog } from "../../api/AppointmentCare";
import { currentUser } from "../Social/socialStore";
import {
    BUCKET_META, CONTACT_RESULT, EMPTY_EXTRA, FLAG_KIND, SURVEY_CHANNEL, UNREACHABLE_LIMIT,
    careApi, failedStreak, surveySendsOf, type CareExtra, type CareFlag, type CareState, type ContactResult, type FlagKind,
} from "./careStore";

/** Mở drawer để xem, gọi ngay, hoặc ghi chú ngay */
export type DrawerIntent = "view" | "call" | "note";
type Channel = "call" | "zalo";

const meName = () => currentUser().name;
const fmtDuration = (seconds: number) =>
    `${Math.floor(seconds / 60).toString().padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;

// ---------------------------------------------------------------------------
// Ghi kết quả liên hệ (sau khi gọi / nhắn Zalo)
// ---------------------------------------------------------------------------
function ContactResultModal({ open, channel, duration, appointmentId, onClose, onSaved }: {
    open: boolean;
    channel: Channel;
    duration: number;
    appointmentId?: number;
    onClose: () => void;
    onSaved: (result: ContactResult) => void;
}) {
    const [result, setResult] = useState<ContactResult | null>(null);
    const [note, setNote] = useState("");
    const [followAt, setFollowAt] = useState<Dayjs | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        // Cuộc gọi có thời lượng thì gần như chắc chắn đã nghe máy
        setResult(channel === "call" && duration > 0 ? "answered" : null);
        setNote("");
        setFollowAt(null);
    }, [open, channel, duration]);

    const save = async () => {
        if (!appointmentId || !result) return;
        if (result === "callback" && !followAt) { message.warning("Chọn thời gian khách hẹn gọi lại"); return; }
        const parts = [
            channel === "call" ? `Gọi điện${duration > 0 ? ` (${fmtDuration(duration)})` : ""}` : "Nhắn Zalo",
            CONTACT_RESULT[result].label,
            result === "callback" && followAt ? `hẹn ${followAt.format("HH:mm DD/MM")}` : "",
        ].filter(Boolean);
        const content = `${parts.join(" — ")}${note.trim() ? `: ${note.trim()}` : ""}`;
        setSaving(true);
        // Ghi vào hành trình (API care-logs) để cả nhóm cùng thấy; loại 1 = Gọi điện, 2 = Nhắn tin
        const res = await createAppointmentCareLog(appointmentId, { content, type: channel === "call" ? 1 : 2 });
        setSaving(false);
        if (!res.status) { message.error(res.msg || "Lưu kết quả liên hệ thất bại"); return; }
        careApi.addAttempt(appointmentId, { by: meName(), result, note: note.trim() || undefined, channel });
        if (result === "callback" && followAt) careApi.setFollowUp(appointmentId, followAt.valueOf(), note.trim() || "Khách hẹn gọi lại", meName());
        onSaved(result);
    };

    return (
        <Modal
            open={open}
            title={channel === "call" ? `Kết quả cuộc gọi${duration > 0 ? ` · ${fmtDuration(duration)}` : ""}` : "Kết quả nhắn Zalo"}
            okText="Lưu kết quả"
            cancelText="Bỏ qua"
            okButtonProps={{ disabled: !result }}
            confirmLoading={saving}
            onOk={save}
            onCancel={onClose}
            destroyOnHidden
        >
            <Radio.Group className="w-full" value={result} onChange={(e) => setResult(e.target.value)}>
                <div className="grid grid-cols-2 gap-2">
                    {(Object.keys(CONTACT_RESULT) as ContactResult[]).map((k) => (
                        <Radio.Button key={k} value={k} className="!rounded-lg text-center">{CONTACT_RESULT[k].label}</Radio.Button>
                    ))}
                </div>
            </Radio.Group>
            {result === "callback" && (
                <DatePicker
                    className="w-full mt-3"
                    showTime={{ format: "HH:mm", minuteStep: 15 }}
                    format="HH:mm DD/MM/YYYY"
                    placeholder="Khách hẹn gọi lại lúc"
                    disabledDate={(d) => d.isBefore(dayjs().startOf("day"))}
                    value={followAt}
                    onChange={setFollowAt}
                />
            )}
            <Input.TextArea
                className="mt-3"
                rows={3}
                placeholder={result && CONTACT_RESULT[result].reached ? "Tình trạng khách sau dịch vụ, ý kiến của khách..." : "Ghi chú (không bắt buộc)"}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={1000}
            />
        </Modal>
    );
}

// ---------------------------------------------------------------------------
// Hẹn chăm sóc lại / báo cảnh báo / xử lý cảnh báo
// ---------------------------------------------------------------------------
function FollowUpModal({ open, appointmentId, current, onClose }: { open: boolean; appointmentId?: number; current?: CareExtra["followUp"]; onClose: () => void }) {
    const [at, setAt] = useState<Dayjs | null>(null);
    const [note, setNote] = useState("");
    useEffect(() => {
        if (!open) return;
        setAt(current ? dayjs(current.at) : dayjs().add(3, "day").hour(9).minute(0));
        setNote(current?.note || "");
    }, [open, current]);
    return (
        <Modal
            open={open}
            title="Hẹn chăm sóc lại"
            okText="Lưu lịch hẹn"
            cancelText="Huỷ"
            onCancel={onClose}
            onOk={() => {
                if (!appointmentId || !at) { message.warning("Chọn ngày chăm sóc lại"); return; }
                if (!note.trim()) { message.warning("Nhập nội dung cần chăm sóc"); return; }
                careApi.setFollowUp(appointmentId, at.valueOf(), note.trim(), meName());
                message.success(`Đã hẹn chăm sóc lại lúc ${at.format("HH:mm DD/MM")}`);
                onClose();
            }}
        >
            <div className="flex flex-wrap gap-2 mb-3">
                {[{ l: "Ngày mai", d: 1 }, { l: "Sau 3 ngày", d: 3 }, { l: "Sau 7 ngày", d: 7 }, { l: "Sau 30 ngày", d: 30 }].map((p) => (
                    <Button key={p.d} size="small" onClick={() => setAt(dayjs().add(p.d, "day").hour(9).minute(0))}>{p.l}</Button>
                ))}
            </div>
            <DatePicker
                className="w-full"
                showTime={{ format: "HH:mm", minuteStep: 15 }}
                format="HH:mm DD/MM/YYYY"
                disabledDate={(d) => d.isBefore(dayjs().startOf("day"))}
                value={at}
                onChange={setAt}
            />
            <Input.TextArea
                className="mt-3"
                rows={3}
                placeholder="Nội dung cần chăm sóc (VD: hỏi lại tình trạng sưng, nhắc lịch tái khám...)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
            />
        </Modal>
    );
}

function FlagModal({ open, appointmentId, preset, onClose }: { open: boolean; appointmentId?: number; preset?: { kind: FlagKind; note: string }; onClose: () => void }) {
    const [kind, setKind] = useState<FlagKind>("complication");
    const [note, setNote] = useState("");
    useEffect(() => {
        if (!open) return;
        setKind(preset?.kind || "complication");
        setNote(preset?.note || "");
    }, [open, preset]);
    return (
        <Modal
            open={open}
            title={<span className="flex items-center gap-2 text-red-600"><TriangleAlert size={18} /> Báo cảnh báo</span>}
            okText="Báo cảnh báo"
            okButtonProps={{ danger: true }}
            cancelText="Huỷ"
            onCancel={onClose}
            onOk={() => {
                if (!appointmentId) return;
                if (!note.trim()) { message.warning("Mô tả tình trạng của khách"); return; }
                careApi.addFlag(appointmentId, kind, note.trim(), meName());
                message.success("Đã báo cảnh báo — khách được đưa lên đầu danh sách");
                onClose();
            }}
        >
            <Select
                className="w-full"
                value={kind}
                onChange={setKind}
                options={(Object.keys(FLAG_KIND) as FlagKind[]).map((k) => ({ value: k, label: FLAG_KIND[k] }))}
            />
            <Input.TextArea
                className="mt-3"
                rows={4}
                placeholder="Mô tả tình trạng (VD: sưng đỏ kéo dài 3 ngày, khách đau nhiều vùng tiêm...)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={1000}
            />
            {kind === "complication" && (
                <Alert
                    className="mt-3"
                    type="warning"
                    showIcon
                    message="Biến chứng cần báo ngay cho Bác sĩ / KTV đã thực hiện và hẹn khách quay lại kiểm tra."
                />
            )}
        </Modal>
    );
}

function ResolveFlagModal({ flag, appointmentId, onClose }: { flag: CareFlag | null; appointmentId?: number; onClose: () => void }) {
    const [note, setNote] = useState("");
    useEffect(() => { setNote(""); }, [flag]);
    return (
        <Modal
            open={!!flag}
            title="Xử lý xong cảnh báo"
            okText="Xác nhận đã xử lý"
            cancelText="Huỷ"
            onCancel={onClose}
            onOk={() => {
                if (!appointmentId || !flag) return;
                if (!note.trim()) { message.warning("Nhập cách đã xử lý"); return; }
                careApi.resolveFlag(appointmentId, flag.at, note.trim(), meName());
                message.success("Đã đóng cảnh báo");
                onClose();
            }}
        >
            {flag && <div className="mb-3 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{flag.note}</div>}
            <Input.TextArea
                rows={3}
                placeholder="Cách xử lý (VD: Bác sĩ đã khám lại, kê thuốc chống viêm, khách ổn định)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={1000}
            />
        </Modal>
    );
}

/** Tình trạng chăm sóc: hạn, hẹn lại, cảnh báo, các lần liên hệ */
function CareStatusPanel({ customer, onFollowUp, onFlag, onResolve, onOpenSurvey }: {
    customer: any;
    onOpenSurvey: () => void;
    onFollowUp: () => void;
    onFlag: () => void;
    onResolve: (f: CareFlag) => void;
}) {
    const care: CareState | undefined = customer?.care;
    const extra: CareExtra = customer?.extra || EMPTY_EXTRA;
    if (!care) return null;
    const meta = BUCKET_META[care.bucket];
    const failed = failedStreak(extra.attempts);
    const sends = surveySendsOf(extra);
    return (
        <div className="px-5 py-4 border-b space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <Tag color={meta.color} className="rounded-full !mr-0">
                    {meta.label}{care.bucket === "overdue" ? ` ${care.lateDays} ngày` : ""}
                </Tag>
                {care.bucket !== "cared" && care.dueAt && (
                    <span className="text-xs text-slate-500">Hạn chăm sóc {dayjs(care.dueAt).format("DD/MM/YYYY")}</span>
                )}
                {extra.attempts.length > 0 && (
                    <span className="text-xs text-slate-500">
                        · Liên hệ {extra.attempts.length} lần, gần nhất {CONTACT_RESULT[extra.attempts[0].result].label.toLowerCase()} {dayjs(extra.attempts[0].at).format("HH:mm DD/MM")}
                    </span>
                )}
                <div className="flex-1" />
                <Button size="small" icon={<CalendarClock size={13} />} onClick={onFollowUp}>
                    {extra.followUp ? "Đổi lịch hẹn lại" : "Hẹn chăm sóc lại"}
                </Button>
                <Button size="small" danger icon={<TriangleAlert size={13} />} onClick={onFlag}>Báo cảnh báo</Button>
            </div>

            {care.surveyPending && (
                <div className={`rounded-lg border px-3 py-2 text-sm flex items-start gap-2 ${care.surveyRemind ? "border-amber-300 bg-amber-50 text-amber-800" : "border-purple-200 bg-purple-50 text-purple-800"} dark:bg-transparent`}>
                    <Send size={15} className="mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <b>{care.surveyRemind ? "Khách chưa đánh giá — cần nhắc lại" : "Đã gửi link, chờ khách đánh giá"}</b>
                        <div className="text-xs opacity-80">
                            Gửi {sends.length} lần · gần nhất {dayjs(sends[0].at).format("HH:mm DD/MM")} qua {SURVEY_CHANNEL[sends[0].channel]} · {sends[0].by}
                        </div>
                    </div>
                    <Button size="small" onClick={onOpenSurvey}>{care.surveyRemind ? "Nhắc lại" : "Xem"}</Button>
                </div>
            )}

            {care.bucket === "unreachable" && (
                <Alert
                    type="warning"
                    showIcon
                    message={`Đã ${failed} lần liên hệ không được (giới hạn ${UNREACHABLE_LIMIT}). Thử nhắn Zalo hoặc hẹn lại ngày khác.`}
                />
            )}

            {extra.followUp && (
                <div className={`rounded-lg border px-3 py-2 text-sm flex items-start gap-2 ${care.followUpDue ? "border-violet-300 bg-violet-50 text-violet-800" : "border-slate-200 bg-slate-50 text-slate-700"} dark:bg-transparent`}>
                    <BellRing size={15} className="mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <b>Hẹn chăm sóc lại {dayjs(extra.followUp.at).format("HH:mm DD/MM/YYYY")}</b>
                        {care.followUpDue ? " · đã đến hẹn" : ""}
                        <div className="text-xs opacity-80">{extra.followUp.note} · {extra.followUp.by}</div>
                    </div>
                    <Tooltip title="Đã chăm sóc xong lịch hẹn lại">
                        <Button
                            size="small"
                            type="text"
                            icon={<CheckCircle2 size={15} />}
                            onClick={() => { careApi.clearFollowUp(customer.id); message.success("Đã hoàn tất lịch hẹn chăm sóc lại"); }}
                        />
                    </Tooltip>
                </div>
            )}

            {care.openFlags.map((f) => (
                <div key={f.at} className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 px-3 py-2 text-sm text-red-700 flex items-start gap-2">
                    <TriangleAlert size={15} className="mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <b>{FLAG_KIND[f.kind]}</b>
                        <div className="whitespace-pre-wrap">{f.note}</div>
                        <div className="text-xs opacity-75">{f.by} · {dayjs(f.at).format("HH:mm DD/MM/YYYY")}</div>
                    </div>
                    <Button size="small" onClick={() => onResolve(f)}>Đã xử lý</Button>
                </div>
            ))}

            {care.lowCsat && care.openFlags.length === 0 && (
                <Alert
                    type="error"
                    showIcon
                    message={`Khách chấm CSAT ${customer.csat}/5 — cần liên hệ lại để xử lý.`}
                    action={<Button size="small" danger onClick={onFlag}>Tạo cảnh báo</Button>}
                />
            )}

            {extra.flags.some((f) => f.resolved) && (
                <details className="text-xs text-slate-500">
                    <summary className="cursor-pointer">Cảnh báo đã xử lý ({extra.flags.filter((f) => f.resolved).length})</summary>
                    <div className="mt-2 space-y-2">
                        {extra.flags.filter((f) => f.resolved).map((f) => (
                            <div key={f.at} className="rounded border border-slate-200 p-2">
                                <div><b>{FLAG_KIND[f.kind]}</b>: {f.note}</div>
                                <div className="text-green-700 mt-1">✓ {f.resolved!.note} — {f.resolved!.by}, {dayjs(f.resolved!.at).format("HH:mm DD/MM")}</div>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}

export default function CustomerCareDrawer({
    open,
    onClose,
    customer,
    intent = "view",
    onUpdated,
}: any) {
    const [tab, setTab] = useState("timeline");
    const [timelineKey, setTimelineKey] = useState(0);
    const [followOpen, setFollowOpen] = useState(false);
    const [flagPreset, setFlagPreset] = useState<{ kind: FlagKind; note: string } | undefined>();
    const [flagOpen, setFlagOpen] = useState(false);
    const [resolving, setResolving] = useState<CareFlag | null>(null);
    const [resultFor, setResultFor] = useState<{ channel: Channel; duration: number } | null>(null);
    const [width, setWidth] = useState(() => Math.min(600, window.innerWidth));

    // Softphone giả lập — giao diện chờ tích hợp tổng đài, chỉ đếm giờ và ghi kết quả
    const [isSoftphoneOpen, setIsSoftphoneOpen] = useState(false);
    const [callState, setCallState] = useState<"idle" | "calling" | "connected" | "ended">("idle");
    const [connectedAt, setConnectedAt] = useState<number | null>(null);
    const [now, setNow] = useState(Date.now());

    const phone: string = customer?.phone || "";
    const callDuration = connectedAt ? Math.max(0, Math.round((now - connectedAt) / 1000)) : 0;

    useEffect(() => {
        const onResize = () => setWidth(Math.min(600, window.innerWidth));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        if (callState !== "connected") return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [callState]);

    const startCall = () => {
        if (!phone) { message.warning("Khách chưa có số điện thoại"); return; }
        if (callState !== "idle") { setIsSoftphoneOpen(true); return; }
        setIsSoftphoneOpen(true);
        setConnectedAt(null);
        setCallState("calling");
        setTimeout(() => {
            setCallState((s) => {
                if (s !== "calling") return s;
                setConnectedAt(Date.now());
                setNow(Date.now());
                return "connected";
            });
        }, 2000);
    };

    const endCall = () => {
        const duration = callState === "connected" ? callDuration : 0;
        setCallState("ended");
        setTimeout(() => {
            setCallState("idle");
            setIsSoftphoneOpen(false);
            setConnectedAt(null);
            setResultFor({ channel: "call", duration });
        }, 600);
    };

    // Mở drawer theo ý định: gọi ngay / ghi chú ngay
    useEffect(() => {
        if (!open) return;
        setTab("timeline");
        if (intent === "call") startCall();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, customer?.id]);

    const openZalo = () => {
        if (!phone) { message.warning("Khách chưa có số điện thoại"); return; }
        window.open(`https://zalo.me/${phone.replace(/\D/g, "")}`, "_blank", "noopener");
        setResultFor({ channel: "zalo", duration: 0 });
    };

    const handleClose = () => {
        if (callState === "calling" || callState === "connected") {
            message.warning("Đang trong cuộc gọi — kết thúc cuộc gọi trước khi đóng");
            setIsSoftphoneOpen(true);
            return;
        }
        onClose();
    };

    return (
        <Drawer
            open={open}
            onClose={handleClose}
            placement="right"
            width={width}
            closable={false}
            maskClosable
            destroyOnHidden
            rootStyle={{
                height: "100vh",
            }}
            styles={{
                body: {
                    padding: 0,
                    overflowY: "auto",
                },
            }}
        >
            <div className="flex min-h-full flex-col">
                {/* Header */}

                <div className="border-b p-5 sm:p-6">

                    <div className="flex justify-between gap-3">

                        <div className="flex gap-4 min-w-0">

                            <Avatar
                                size={56}
                                className="shrink-0"
                                style={{
                                    background: "#F3E8FF",
                                    color: "#9333EA",
                                    fontSize: 28,
                                    fontWeight: 700,
                                }}
                            >
                                {customer?.avatar}
                            </Avatar>

                            <div className="min-w-0">

                                <div className="flex items-center gap-2 flex-wrap">

                                    <h2 className="text-[18px] font-semibold leading-none text-000000e0">
                                        {customer?.name}
                                    </h2>

                                    <Tag
                                        color={customer?.is_cared ? "success" : "warning"}
                                        className="!text-[10px] !px-1.5 !py-0 rounded"
                                    >
                                        {customer?.status}
                                    </Tag>

                                    {customer?.csat !== null && customer?.csat !== undefined && (
                                        <Tag color={customer.csat < 3 ? "error" : "gold"} className="!text-[10px] !px-1.5 !py-0 rounded">
                                            CSAT {customer.csat}
                                        </Tag>
                                    )}

                                </div>

                                <div className="space-y-1 mt-2 text-[13px] text-slate-600">

                                    <div className="flex gap-2 items-center">
                                        <Phone size={14} />
                                        {phone ? <Typography.Text copyable className="!text-[13px]">{phone}</Typography.Text> : "—"}
                                    </div>

                                    <div className="flex gap-2 items-center">
                                        <FileText size={14} />
                                        Mã lịch hẹn: {customer?.code} · {customer?.date}
                                    </div>

                                    <div className="flex gap-2 items-start text-blue-600">
                                        <Calendar size={14} className="mt-0.5 shrink-0" />
                                        Dịch vụ đã làm: {customer?.service}
                                    </div>

                                    {customer?.doctor && (
                                        <div className="flex gap-2 items-center">
                                            <Stethoscope size={14} />
                                            Người thực hiện: {customer.doctor}
                                        </div>
                                    )}

                                </div>

                            </div>

                        </div>

                        <Button
                            shape="circle"
                            className="shrink-0"
                            icon={<X size={16} />}
                            onClick={handleClose}
                        />

                    </div>

                </div>

                {/* Action — Gọi điện (giả lập tổng đài) / Zalo (mở zalo.me) / Messenger (chưa có liên kết) */}

                <div className="grid grid-cols-3 gap-3 p-5 border-b">

                    <Button
                        onClick={startCall}
                        disabled={!phone}
                        className="h-[54px] text-[13px] flex flex-col items-center justify-center gap-1 p-2 rounded-lg bg-green-50 text-green-700 hover:!bg-green-100 transition-colors border-none"
                        icon={<Phone size={18} />}
                    >
                        Gọi điện
                    </Button>

                    <Button
                        onClick={openZalo}
                        disabled={!phone}
                        className="h-[54px] text-[13px] flex flex-col items-center justify-center gap-1 p-2 rounded-lg bg-blue-50 text-blue-700 hover:!bg-blue-100 transition-colors border-none"
                        icon={<MessageCircle size={18} />}
                    >
                        Zalo
                    </Button>

                    <Tooltip title="Khách chưa liên kết Facebook — chờ tích hợp Messenger">
                        <Button
                            disabled
                            className="h-[54px] text-[13px] border-none flex flex-col items-center justify-center gap-1 p-2 rounded-lg bg-indigo-50 text-indigo-700 transition-colors"
                            icon={<MessageCircle size={18} />}
                        >
                            Messenger
                        </Button>
                    </Tooltip>

                </div>

                <CareStatusPanel
                    customer={customer}
                    onFollowUp={() => setFollowOpen(true)}
                    onFlag={() => {
                        setFlagPreset(customer?.care?.lowCsat && !customer?.care?.openFlags.length
                            ? { kind: "complaint", note: `Khách chấm CSAT ${customer.csat}/5. ${customer?.survey_data?.care_note || ""}`.trim() }
                            : undefined);
                        setFlagOpen(true);
                    }}
                    onResolve={setResolving}
                    onOpenSurvey={() => setTab("csat")}
                />

                {/* Softphone */}
                <div className={`fixed bottom-6 right-6 w-72 bg-white rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.3)] border border-gray-200 z-50 transition-transform duration-300 transform flex flex-col overflow-hidden ${isSoftphoneOpen ? 'translate-y-0' : 'translate-y-[120%]'}`}>
                    <div className="bg-gray-900 text-white p-3 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Phone size={16} className="text-blue-400" />
                            <span className="font-semibold text-sm">Cuộc gọi chăm sóc</span>
                        </div>
                        <button onClick={() => setIsSoftphoneOpen(false)} className="text-gray-400 hover:text-white" title="Thu nhỏ"><X size={16} /></button>
                    </div>

                    <div className="p-6 flex flex-col items-center bg-gray-900 text-white relative">
                        <h4 className="text-gray-400 text-xs mb-1 z-10">
                            {callState === 'calling' ? 'Đang kết nối...' : callState === 'connected' ? 'Đang đàm thoại' : callState === 'ended' ? 'Đã kết thúc' : 'Sẵn sàng'}
                        </h4>
                        <h2 className="text-xl font-bold mb-1 z-10">{phone}</h2>
                        <p className="text-xs text-gray-300 mb-4 z-10">{customer?.name}</p>
                        <div className="w-32 h-32 rounded-full border-2 border-green-500 pulse-ring flex items-center justify-center mb-4 relative">
                            <div className={`text-2xl font-mono z-10 ${callState === 'connected' ? 'text-green-400' : 'text-gray-500'}`}>
                                {fmtDuration(callDuration)}
                            </div>
                        </div>
                        {callState === "idle" ? (
                            <button onClick={startCall} className="w-12 h-12 rounded-full flex items-center justify-center shadow transition bg-green-500 hover:bg-green-600 hover:scale-105">
                                <Phone size={20} color="#fff" />
                            </button>
                        ) : (
                            <button onClick={endCall} disabled={callState === 'ended'} className={`w-12 h-12 rounded-full flex items-center justify-center text-xl shadow transition z-10 ${callState === 'ended' ? 'bg-gray-600 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600 hover:scale-105'}`}>
                                <PhoneOff size={20} color="#fff" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Nút nổi khi thu nhỏ softphone mà cuộc gọi vẫn đang diễn ra */}
                {!isSoftphoneOpen && (callState === "calling" || callState === "connected") && (
                    <button
                        onClick={() => setIsSoftphoneOpen(true)}
                        className="fixed bottom-6 right-6 z-50 rounded-full bg-green-600 text-white px-4 py-2 shadow-lg flex items-center gap-2 text-sm font-mono"
                    >
                        <Phone size={14} /> {fmtDuration(callDuration)}
                    </button>
                )}

                <div className="flex-1">
                    <Tabs
                        activeKey={tab}
                        onChange={setTab}
                        className="customer-care-tabs flex-1"
                        items={[
                            {
                                key: "timeline",
                                label: "Hành trình (Timeline)",
                                children: <TimelineTab key={timelineKey} appointmentId={customer?.id} autoFocus={intent === "note"} />,
                            },
                            {
                                key: "csat",
                                label: "Khảo sát CSAT",
                                children: (
                                    <CsatTab
                                        appointmentId={customer?.id}
                                        survey={customer?.survey_data}
                                        isCared={customer?.is_cared}
                                        caredBy={customer?.cared_by_staff_name}
                                        caredAt={customer?.cared_at}
                                        extra={customer?.extra}
                                        customerName={customer?.name}
                                        service={customer?.service}
                                        phone={phone}
                                        onUpdated={onUpdated}
                                        onLogged={() => setTimelineKey((k) => k + 1)}
                                    />
                                ),
                            },
                            {
                                key: "gallery",
                                label: "Hình ảnh (Gallery)",
                                children: <GalleryTab appointmentId={customer?.id} />,
                            },
                        ]}
                    />
                </div>
            </div>

            <ContactResultModal
                open={!!resultFor}
                channel={resultFor?.channel || "call"}
                duration={resultFor?.duration || 0}
                appointmentId={customer?.id}
                onClose={() => setResultFor(null)}
                onSaved={(result) => {
                    setResultFor(null);
                    setTimelineKey((k) => k + 1);
                    if (CONTACT_RESULT[result].reached && result === "answered" && !customer?.is_cared) {
                        message.success("Đã lưu kết quả — gửi link cho khách tự đánh giá, hoặc điền phiếu khảo sát ngay", 4);
                        setTab("csat");
                    } else {
                        message.success("Đã lưu kết quả liên hệ");
                    }
                }}
            />
            <FollowUpModal open={followOpen} appointmentId={customer?.id} current={customer?.extra?.followUp} onClose={() => setFollowOpen(false)} />
            <FlagModal open={flagOpen} appointmentId={customer?.id} preset={flagPreset} onClose={() => setFlagOpen(false)} />
            <ResolveFlagModal flag={resolving} appointmentId={customer?.id} onClose={() => setResolving(null)} />
        </Drawer>
    );
}
